import type { MailSendPayload } from "@/lib/types/compose";

const LEGACY_LS_KEY = "mail_outbox_queue_v2";
const LEGACY_SS_KEY = "mail_outbox_queue_v1";

const DB_NAME = "aurelia_mail_outbox_v1";
const DB_VERSION = 1;
const STORE = "queue";
const QUEUE_KEY = "items";

export type OutboxSendStatus = "pending" | "sending" | "failed";

export type OutboxQueueItem = MailSendPayload & {
  id: string;
  accountId: number;
  status: OutboxSendStatus;
  error?: string;
  createdAt: number;
  fromEmail?: string;
};

export type OutboxEnqueuePayload = MailSendPayload & {
  accountId: number;
  /** Compte d’envoi, pour le journal (pas envoyé au SMTP). */
  fromEmail?: string;
};

export type OutboxServerAck = { ok: true } | { ok: false; error: string };

export type OutboxEnqueueResult =
  | { ok: true; item: OutboxQueueItem; accepted?: Promise<OutboxServerAck> }
  | { ok: false; error: string };

/** File déjà lue. null = pas encore de mutation dans cette page. */
let memory: OutboxQueueItem[] | null = null;
/** Ids retirés avant la fin de l’hydratation : le disque ne doit pas les remettre. */
const removedIds = new Set<string>();
/** Vidage demandé avant la fin de l’hydratation : ne pas réimporter le disque. */
let wiped = false;
let persistChain: Promise<void> = Promise.resolve();
let dbPromise: Promise<IDBDatabase> | null = null;
let hydratePromise: Promise<OutboxQueueItem[]> | null = null;

function isStatus(value: unknown): value is OutboxSendStatus {
  return value === "pending" || value === "sending" || value === "failed";
}

/** Reprise après fermeture : un envoi interrompu redevient à faire. */
function itemsFromDisk(raw: unknown): OutboxQueueItem[] {
  if (!Array.isArray(raw)) return [];
  const items: OutboxQueueItem[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as OutboxQueueItem;
    if (!isStatus(row.status) || typeof row.id !== "string" || row.id === "") continue;
    if (removedIds.has(row.id)) continue;
    items.push(
      row.status === "sending" ? { ...row, status: "pending", error: undefined } : row
    );
  }
  return items;
}

function mergeById(primary: OutboxQueueItem[], secondary: OutboxQueueItem[]): OutboxQueueItem[] {
  const seen = new Set(primary.map((item) => item.id));
  return [...primary, ...secondary.filter((item) => item.id !== "" && !seen.has(item.id))];
}

function readLegacy(): OutboxQueueItem[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw =
      localStorage.getItem(LEGACY_LS_KEY) ||
      (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(LEGACY_SS_KEY) : null);
    if (!raw) return [];
    return itemsFromDisk(JSON.parse(raw) as unknown);
  } catch {
    return [];
  }
}

function clearLegacy(): void {
  try {
    localStorage.removeItem(LEGACY_LS_KEY);
    sessionStorage.removeItem(LEGACY_SS_KEY);
  } catch {
    /* quota ou navigation privée */
  }
}

function readCurrent(): OutboxQueueItem[] {
  return memory ?? readLegacy();
}

function openDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB indisponible."));
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error("IndexedDB indisponible."));
    };
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
  });
  return dbPromise;
}

async function readIdb(): Promise<OutboxQueueItem[]> {
  if (typeof indexedDB === "undefined") return [];
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const request = tx.objectStore(STORE).get(QUEUE_KEY);
    request.onsuccess = () => {
      const row = request.result as { items?: unknown } | undefined;
      resolve(itemsFromDisk(row?.items));
    };
    request.onerror = () => reject(request.error ?? new Error("Lecture IndexedDB impossible."));
  });
}

async function writeIdb(items: OutboxQueueItem[]): Promise<boolean> {
  if (typeof indexedDB === "undefined") return false;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const request = tx.objectStore(STORE).put({ id: QUEUE_KEY, items });
      request.onerror = () => reject(request.error ?? new Error("Écriture IndexedDB impossible."));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Écriture IndexedDB impossible."));
      tx.onabort = () => reject(tx.error ?? new Error("Écriture IndexedDB interrompue."));
    });
    return true;
  } catch {
    return false;
  }
}

function persist(items: OutboxQueueItem[]): Promise<boolean> {
  const snapshot = items;
  const run = persistChain.then(() => writeIdb(snapshot));
  persistChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/** L’envoi attend que la copie disque de la file soit posée. */
export function outboxPersistSettled(): Promise<void> {
  return persistChain;
}

function commit(items: OutboxQueueItem[], droppedIds: string[] = []): OutboxQueueItem[] {
  for (const id of droppedIds) {
    if (id !== "") removedIds.add(id);
  }
  if (memory) {
    const nextIds = new Set(items.map((item) => item.id));
    for (const item of memory) {
      if (!nextIds.has(item.id)) removedIds.add(item.id);
    }
  }
  memory = items;
  void persist(items);
  return items;
}

/**
 * Reprend la file IndexedDB (et l’ancienne copie localStorage, une fois).
 * IndexedDB tient une pièce de 20 Mo. localStorage non : le quota du navigateur
 * coupait vers 3 Mo, téléphone et ordinateur.
 */
export function hydrateOutboxQueue(): Promise<OutboxQueueItem[]> {
  if (!hydratePromise) {
    hydratePromise = (async () => {
      let stored: OutboxQueueItem[] = [];
      try {
        stored = await readIdb();
      } catch {
        stored = [];
      }
      const disk = mergeById(readLegacy(), stored).filter((item) => !removedIds.has(item.id));
      const next = wiped ? [] : (memory ? mergeById(memory, disk) : disk);
      memory = next;
      const saved = await persist(next);
      if (saved) clearLegacy();
      return next;
    })().catch((err: unknown) => {
      hydratePromise = null;
      throw err;
    });
  }
  return hydratePromise;
}

export function readOutboxQueue(): OutboxQueueItem[] {
  return readCurrent();
}

export function enqueueOutboxItem(payload: OutboxEnqueuePayload): OutboxEnqueueResult {
  const item: OutboxQueueItem = {
    ...payload,
    id: crypto.randomUUID(),
    status: "pending",
    createdAt: Date.now(),
  };
  commit([item, ...readCurrent()]);
  return { ok: true, item };
}

export function updateOutboxItem(
  id: string,
  patch: Partial<Pick<OutboxQueueItem, "status" | "error">>
): OutboxQueueItem[] {
  return commit(readCurrent().map((item) => (item.id === id ? { ...item, ...patch } : item)));
}

export function removeOutboxItem(id: string): OutboxQueueItem[] {
  return commit(
    readCurrent().filter((item) => item.id !== id),
    [id]
  );
}

export function retryOutboxItem(id: string): OutboxQueueItem[] {
  return updateOutboxItem(id, { status: "pending", error: undefined });
}

export function mergeFailedOutboxItems(items: OutboxQueueItem[]): OutboxQueueItem[] {
  const current = readCurrent();
  const ids = new Set(current.map((item) => item.id));
  const extra = items.filter((item) => item.id !== "" && !ids.has(item.id));
  if (extra.length === 0) return current;
  return commit([...extra, ...current]);
}

export function activeOutboxItems(
  items: OutboxQueueItem[],
  accountId?: number
): OutboxQueueItem[] {
  return items.filter((item) => accountId == null || item.accountId === accountId);
}

/** Vidage du cache Mail : la file part avec, comme l’ancienne clé localStorage. */
export function clearOutboxQueueStore(): Promise<void> {
  wiped = true;
  for (const item of readCurrent()) removedIds.add(item.id);
  memory = [];
  clearLegacy();
  return persist([]).then(() => undefined);
}
